import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Appointment, Patient, Doctor, Service, DashboardStats } from '../types';
import { clinicAPI } from '../services/api';
import toast from 'react-hot-toast';

interface DataContextType {
  // Data
  appointments: Appointment[];
  patients: Patient[];
  doctors: Doctor[];
  services: Service[];
  stats: DashboardStats;
  
  // Actions
  addAppointment: (appointment: Omit<Appointment, 'id'>) => void;
  updateAppointment: (id: string, updates: Partial<Appointment>) => void;
  deleteAppointment: (id: string) => void;
  
  addPatient: (patient: Omit<Patient, 'id' | 'createdAt' | 'appointments'>) => void;
  updatePatient: (id: string, updates: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  
  addDoctor: (doctor: Omit<Doctor, 'id'>) => void;
  updateDoctor: (id: string, updates: Partial<Doctor>) => void;
  deleteDoctor: (id: string) => void;
  
  // Loading states
  isLoading: boolean;
  
  // Refresh data
  refreshData: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate stats from real data
  const calculateStats = (): DashboardStats => {
    const now = new Date();
    const today = now.toDateString();
    
    const todaysAppointments = appointments.filter(apt => 
      new Date(apt.date).toDateString() === today
    );
    
    const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed');
    const completedAppointments = appointments.filter(apt => apt.status === 'completed');
    const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled');
    const upcomingAppointments = appointments.filter(apt => 
      new Date(apt.date) > now && apt.status !== 'cancelled'
    );
    
    return {
      totalPatients: patients.length,
      todayAppointments: todaysAppointments.length,
      monthlyRevenue: completedAppointments.length * 150, // Average $150 per appointment
      upcomingAppointments: upcomingAppointments.length,
      completedAppointments: completedAppointments.length,
      cancelledAppointments: cancelledAppointments.length
    };
  };

  const stats = calculateStats();

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      
      try {
        // Load data from API (NO localStorage!)
        const [appointmentsRes, patientsRes, doctorsRes, servicesRes] = await Promise.all([
          clinicAPI.getAppointments(),
          clinicAPI.getPatients(),
          clinicAPI.getDoctors(),
          clinicAPI.getServices(),
        ]);
        
        setAppointments(appointmentsRes.success ? appointmentsRes.data || [] : []);
        setPatients(patientsRes.success ? patientsRes.data || [] : []);
        setDoctors(doctorsRes.success ? doctorsRes.data || [] : []);
        setServices(servicesRes.success ? servicesRes.data || [] : []);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load clinic data');
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, []);

  // Appointment actions
  const addAppointment = (appointmentData: Omit<Appointment, 'id'>) => {
    const newAppointment: Appointment = {
      ...appointmentData,
      id: Date.now().toString(),
    };
    setAppointments(prev => [...prev, newAppointment]);
  };

  const updateAppointment = (id: string, updates: Partial<Appointment>) => {
    setAppointments(prev => 
      prev.map(apt => apt.id === id ? { ...apt, ...updates } : apt)
    );
  };

  const deleteAppointment = (id: string) => {
    setAppointments(prev => prev.filter(apt => apt.id !== id));
  };

  // Patient actions
  const addPatient = (patientData: Omit<Patient, 'id' | 'createdAt' | 'appointments'>) => {
    const newPatient: Patient = {
      ...patientData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      appointments: []
    };
    setPatients(prev => [...prev, newPatient]);
  };

  const updatePatient = (id: string, updates: Partial<Patient>) => {
    setPatients(prev => 
      prev.map(patient => patient.id === id ? { ...patient, ...updates } : patient)
    );
  };

  const deletePatient = (id: string) => {
    setPatients(prev => prev.filter(patient => patient.id !== id));
    // Also remove appointments for this patient
    setAppointments(prev => prev.filter(apt => apt.patientId !== id));
  };

  // Doctor actions
  const addDoctor = (doctorData: Omit<Doctor, 'id'>) => {
    const newDoctor: Doctor = {
      ...doctorData,
      id: Date.now().toString(),
    };
    setDoctors(prev => [...prev, newDoctor]);
  };

  const updateDoctor = (id: string, updates: Partial<Doctor>) => {
    setDoctors(prev => 
      prev.map(doctor => doctor.id === id ? { ...doctor, ...updates } : doctor)
    );
  };

  const deleteDoctor = (id: string) => {
    setDoctors(prev => prev.filter(doctor => doctor.id !== id));
    // Also cancel appointments for this doctor
    setAppointments(prev => 
      prev.map(apt => 
        apt.doctorId === id 
          ? { ...apt, status: 'cancelled' as const }
          : apt
      )
    );
  };

  const refreshData = () => {
    // In a real app, this would refresh from the API
    // For now, we'll just trigger a re-render
    setAppointments([...appointments]);
    setPatients([...patients]);
    setDoctors([...doctors]);
  };

  const value: DataContextType = {
    appointments,
    patients,
    doctors,
    services,
    stats,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    addPatient,
    updatePatient,
    deletePatient,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    isLoading,
    refreshData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};